<template>
    <ul class="flex flex-col">
        <li v-if="showSectionHeaders" class="scaffold-details-small text-ink-muted px-2.5 pt-2 pb-1" data-testid="picker-self-header">{{ selfLabel }}</li>
        <li v-for="review in selfReviews" :key="review.id" class="row-item">
            <PickerRow
                :review="review"
                :current-conversation-hashid="currentConversationHashid"
                :is-busy="isBusy"
                :start-disabled="startDisabled"
                :highlighted="preselectedCtaId != null && review.performance_moment_cta_id === preselectedCtaId"
                @row-clicked="emit('row-clicked', $event)" />
        </li>
        <li v-if="hasTeamRow" class="row-item">
            <button
                type="button"
                class="group flex w-full items-center gap-3 rounded-md px-2.5 py-4 text-start transition-colors hover:bg-surface-hover"
                :class="{ 'bg-surface-sunken': preselectedTeam }"
                data-testid="picker-team-row"
                @click="emit('open-team')">
                <span class="grow min-w-0 body-small text-ink truncate">{{ teamLabel }}</span>
                <span class="flex items-center gap-3 shrink-0">
                    <StatusChip size="2xs" :state="teamStatusState" class="min-w-24 justify-center transition-colors group-hover:bg-white">{{ teamStatus }}</StatusChip>
                    <i
                        class="bi bi-arrow-right text-sm/none text-ink opacity-0 -translate-x-1 transition-[opacity,translate] duration-150 group-hover:opacity-100 group-hover:translate-x-0"
                        aria-hidden="true"></i>
                </span>
            </button>
        </li>
    </ul>
</template>

<script setup lang="ts">
import StatusChip from "~vue/components/StatusChip.vue";
import { isReviewCompleted } from "~vue/Moments/composables/useReviewGroupStatus";
import type { DecoratedPerformanceReview } from "~vue/types/performance_management";
import { computed } from "vue";

import PickerRow from "../ChatPerformancePickerRow.vue";

interface Props {
    selfReviews: DecoratedPerformanceReview[];
    teamReviews: DecoratedPerformanceReview[];
    selfLabel: string;
    teamLabel: string;
    /** Keeps the team row when nobody has been added yet, because the person phase can add them. */
    showEmptyTeamRow?: boolean;
    currentConversationHashid?: string;
    isBusy?: boolean;
    startDisabled?: boolean;
    preselectedCtaId?: number | null;
    preselectedTeam?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    showEmptyTeamRow: false,
    currentConversationHashid: "",
    isBusy: false,
    startDisabled: false,
    preselectedCtaId: null,
    preselectedTeam: false,
});

const emit = defineEmits<{ "row-clicked": [review: DecoratedPerformanceReview]; "open-team": [] }>();

const hasTeamRow = computed(() => !!props.teamReviews.length || props.showEmptyTeamRow);

const completedReviewCount = computed(() => props.teamReviews.filter(isReviewCompleted).length);
const teamStatus = computed(() => (props.teamReviews.length ? `${completedReviewCount.value} of ${props.teamReviews.length}` : "None added"));
const teamStatusState = computed(() => {
    if (!props.teamReviews.length) {
        return "neutral";
    }
    if (completedReviewCount.value === 0) {
        return "pending";
    }
    return completedReviewCount.value === props.teamReviews.length ? "done" : "active";
});

// The team row names itself, so the self rows only need a header once both sections render.
const showSectionHeaders = computed(() => !!props.selfReviews.length && hasTeamRow.value);
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .row-item {
        @apply border-b border-border transition-colors;
    }

    .row-item:last-child {
        @apply border-b-0;
    }
}

/* The hairline touching a hovered row fades out (unlayered so it beats the border utility). */
.row-item:hover,
.row-item:has(+ .row-item:hover) {
    border-color: transparent;
}
</style>
